import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Reveal } from "@/components/Reveal";
import { trustPages } from "@/data/trust";

export default function TrustHub() {
  return (
    <PageLayout
      seoTitle="Aliwer Trust Center | Security, Privacy & AI Governance"
      seoDescription="The Aliwer Trust Center: our security practices, privacy and data protection, AI governance, compliance frameworks, subprocessors, and vulnerability disclosure."
      path="/trust"
      eyebrow="Trust Center"
      title="Security, privacy, and governance — in the open."
      subtitle="Everything you need to evaluate how Aliwer protects data and operates responsibly, in one place."
      testid="trust-hub"
    >
      <Reveal>
        <Link
          to="/trust/soc2-journey"
          data-testid="trust-soc2-journey-card"
          className="group mb-10 flex flex-col sm:flex-row sm:items-center gap-6 rounded-2xl border border-white/15 bg-surface p-7 sm:p-8 hover:bg-[#232326] transition-colors duration-500"
        >
          <Icons.BadgeCheck size={28} className="text-paper shrink-0" strokeWidth={1.3} />
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-display text-xl tracking-tight text-paper">Our SOC 2 journey</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-yellow-400/40 text-yellow-400 px-2.5 py-0.5 text-[11px] uppercase tracking-widest">
                <Icons.Loader size={11} /> In progress
              </span>
              <span className="text-[11px] uppercase tracking-widest text-fog">Updated January 12, 2027</span>
            </div>
            <p className="mt-2 text-sm text-fog leading-relaxed max-w-2xl">
              We're pursuing SOC 2 Type II and documenting every phase in the open — scoping, gap assessment,
              controls, evidence, and audit. See exactly where we are today.
            </p>
          </div>
          <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-paper shrink-0">
            View the timeline
            <Icons.ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </Link>
      </Reveal>

      <div className="grid gap-px bg-white/10 border border-white/10 sm:grid-cols-2 lg:grid-cols-3 rounded-2xl overflow-hidden">
        {trustPages.map((t, i) => {
          const Icon = Icons[t.icon] || Icons.ShieldCheck;
          return (
            <Reveal key={t.slug} delay={i * 0.03}>
              <Link
                to={`/trust/${t.slug}`}
                data-testid={`trust-card-${t.slug}`}
                className="group flex flex-col h-full bg-surface p-7 hover:bg-[#232326] transition-colors duration-500"
              >
                <Icon size={20} className="text-paper mb-5" strokeWidth={1.4} />
                <h2 className="font-display text-lg tracking-tight text-paper mb-2">{t.title}</h2>
                <p className="text-sm text-fog leading-relaxed flex-1">{t.intro}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-paper">
                  View
                  <Icons.ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </PageLayout>
  );
}
